import { Box, Grid, Paper, Typography } from '@material-ui/core/';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import Alert from '@material-ui/lab/Alert';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

// import EnvironmentAnimations from 'components/animations/EnvironmentAnimations';
import LineMiniChart from 'components/charts/LineMiniChart';
import TemperatureChart from 'components/charts/TemperatureChart';
import Loading from 'components/animations/Loading';
// import PillarsAnimations from 'components/animations/PillarsAnimations';
import Dropdown from 'components/Dropdown';
import SenslaChart from 'components/SenslaChart';
import useSenslaApi from 'hooks/useSenslaApi';
import { ctx } from 'store';
import { colors } from 'styles';

import { constancyText, efficacyText, energyText, senslaScoreText } from 'utils/text';

import { useStyles } from './styles';

const ALL_CHANNELS = 'All-channels';
const ALL_GROUPS = 'All-teams';

const Pillars: React.FC = () => {
  const {
    teams: { currentTeam },
  } = useContext(ctx.store);
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [daysSelected, setDaysSelected] = useState(1);
  const [channels, setChannels] = useState<{ id: string }[]>([]);
  const [groups, setGroups] = useState<{ id: string }[]>([]);
  const queryParams: any = queryString.parse(location.search);
  const { channelId, groupId } = queryParams;
  const [channel, setChannel] = useState(channelId ? channelId : ALL_CHANNELS);
  const [group, setGroup] = useState(groupId ? groupId : ALL_GROUPS);
  const [selectedQuery, setSelectedQuery] = useState('');
  const query = queryString.stringify(
    {
      days: daysSelected,
      channel: channel === ALL_CHANNELS ? null : channel,
      group: group === ALL_GROUPS ? null : group,
      team: currentTeam.teamId ? currentTeam.teamId : currentTeam.tenantId,
      platform: currentTeam.teamId ? 'slack' : 'microsoft',
    },
    {
      skipNull: true,
    }
  );

  const [{ data: activityReport, loading }] = useSenslaApi(`pillars?${query}`, 'GET', true);

  useEffect(() => {
    const data = [
      ...(currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels)
        .map((ch: any) => {
          if (ch.monitored) {
            return { id: ch.channelId };
          }
        })
        .filter((c: any) => c),
    ];
    const groupsData = [
      ...(currentTeam.teamId ? [] : currentTeam.msGroups)
        .filter((gr: any) => gr.monitored)
        .map((group: any) => {
          return { id: group.groupId };
        }),
    ];
    onChange(channelId, 'channels', true);
    setSelectedChannel(channelId);
    setChannels(data);
    if (!currentTeam.teamId) {
      onChange(groupId, 'teams', true);
      setSelectedGroup(groupId);
      setGroups(groupsData);
    }
    setDaysSelected(1);
  }, []);

  const dataReport = useMemo(() => {
    if (!activityReport) return [];
    return activityReport;
  }, [activityReport]);

  // Counters
  const onChange = (value: string, type: string, fromEffect?: boolean) => {
    if (!fromEffect) {
      let groupQuery =
        group || type === 'teams' ? `groupId=${type === 'teams' ? value : group}` : '';
      let channelQuery =
        channel || type === 'channels' ? `channelId=${type === 'channels' ? value : channel}` : '';

      groupQuery = groupQuery === 'groupId=All-teams' ? '' : groupQuery;
      channelQuery = channelQuery === 'channelId=All-channels' ? '' : channelQuery;

      let completeQuery = '';
      if (groupQuery !== '' && channelQuery !== '') {
        completeQuery = `?${groupQuery}&${channelQuery}`;
      } else if (groupQuery === '') {
        completeQuery = `?${channelQuery}`;
      } else if (channelQuery === '') {
        completeQuery = `?${groupQuery}`;
      }

      setSelectedQuery(completeQuery);

      history.push(`/dashboard/pillars${completeQuery}`);
    }

    if (type === 'channels') {
      setSelectedChannel(value);
      const filteredChannel = (currentTeam.teamId
        ? currentTeam.channels
        : currentTeam.msChannels
      ).filter((channel: any) => channel.channelId === value)[0];
      if (filteredChannel) {
        setChannel(filteredChannel.channelId);
      } else {
        setChannel('');
      }
    } else if (type === 'teams') {
      setSelectedGroup(value);
      const filteredGroup = currentTeam.msGroups.filter((group: any) => group.groupId === value)[0];
      if (filteredGroup) {
        setGroup(filteredGroup.groupId);
      } else {
        setGroup('');
      }
    }
  };

  const dropdownChannels = useMemo(() => {
    if (!groupId || groupId === '') {
      return channels;
    }
    return currentTeam.msGroups
      .find((group: any) => group.groupId === groupId)
      .channels.map((channel: any) => ({ id: channel }));
  }, [groupId, channels]);

  return (
    <div style={{ height: '100vh' }}>
      <Box display="flex" p={1} alignItems="center" className={classes.header}>
        <Box p={1} flexGrow={1}>
          <Typography component="h1" variant="h5">
            Sensla Pillars
          </Typography>
        </Box>
        {!currentTeam.teamId ? (
          <Box p={1} flexShrink={0} className={classes.channelSelect}>
            <Dropdown
              addWorkspace
              data={groups}
              translate={currentTeam.msGroups}
              type="teams"
              setValue={onChange}
              value={selectedGroup}
              autocomplete
              defaultValue={groupId}
            />
          </Box>
        ) : null}
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            addWorkspace
            data={dropdownChannels}
            translate={currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels}
            type="channels"
            setValue={onChange}
            value={selectedChannel}
            autocomplete
            defaultValue={channelId}
          />
        </Box>
      </Box>
      {!loading ? (
        <>
          <ReactTooltip multiline={true} />
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <Paper className={classes.senslaChartGrid}>
                <Box p={1} flexGrow={1} flexDirection="row" className={classes.titleBox}>
                  <Typography component="h2" className={`${classes.subtitle} no-translate`}>
                    Sensla Score
                  </Typography>
                  <Typography
                    component="h3"
                    variant="h5"
                    className={classes.whatIs}
                    data-tip={`Sensla Score is the metric that will allow you to understand<br /> how your workspace
                    is performing in terms of communication, <br />revealing information and findings
                    based on your team's energy,<br /> response effectiveness, and consistency in the
                    different channels. <br />The Sensla Score history is a week-by-week snapshot of your
                    team in.`}
                  >
                    What is it?
                  </Typography>
                </Box>
                <Grid className={classes.rowGrid}>
                  <Grid item xs={7}>
                    <Typography className={classes.classTextEnv}>
                      {dataReport.length
                        ? dataReport[dataReport.length - 1].compound > 0.8
                          ? 'EXCELLENT JOB'
                          : dataReport[dataReport.length - 1].compound > 0.6
                          ? 'GOOD JOB'
                          : dataReport[dataReport.length - 1].compound > 0.4
                          ? 'IT SHOULD BE BETTER'
                          : 'WE MUST DO BETTER'
                        : ''}
                    </Typography>
                    <LineMiniChart
                      data={
                        dataReport.length
                          ? dataReport.map((item: any) => {
                              return {
                                x: item.week,
                                y: item.compound,
                              };
                            })
                          : []
                      }
                      color={colors.blueBrand}
                      strokeWidth={3}
                      height={150}
                    />
                    <Alert
                      severity={
                        dataReport.length && dataReport[dataReport.length - 1].compound > 0.6
                          ? 'success'
                          : 'warning'
                      }
                    >
                      {senslaScoreText(
                        dataReport.length
                          ? dataReport.map((item: any) => {
                              return {
                                x: item.week,
                                senslaScore: item.compound,
                                energy: item.energy,
                                efficacy: item.efficacy,
                                constancy: item.constancy,
                              };
                            })
                          : []
                      )}
                    </Alert>
                    {/* <Typography component="h2" className={classes.text}>
                      {senslaScoreText(
                        dataReport.length
                          ? dataReport.map((item: any) => {
                              return {
                                x: item.week,
                                senslaScore: item.compound,
                                energy: item.energy,
                                efficacy: item.efficacy,
                                constancy: item.constancy,
                              };
                            })
                          : []
                      )}
                    </Typography> */}
                  </Grid>
                  <Grid item xs={6} style={{ textAlign: 'center' }}>
                    <SenslaChart
                      data={
                        dataReport.length
                          ? [
                              dataReport[dataReport.length - 1].compound,
                              dataReport[dataReport.length - 1].energy,
                              dataReport[dataReport.length - 1].efficacy,
                              dataReport[dataReport.length - 1].constancy,
                            ]
                          : []
                      }
                      height={400}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper className={classes.senslaChartGrid}>
                <Box p={1} flexGrow={1} flexDirection="column" className={classes.titleBox}>
                  <Typography component="h2" className={`${classes.subtitle} no-translate`}>
                    Work Environment
                  </Typography>
                  <Typography
                    component="h3"
                    variant="h5"
                    className={classes.whatIs}
                    data-tip={`The work environment is considered a key part of performance and is <br />
                    calculated based on the sentiment manifested in each channel or workspace. <br />
                    Each week you will be able to see what good activities, <br />
                    accomplishments or downfalls are manifested in the team. `}
                  >
                    What is it?
                  </Typography>
                </Box>
                <Grid className={classes.rowGridEnv}>
                  {/* <Typography component="h2" className={classes.classTextEnv}>
                    {dataReport.length
                      ? dataReport[dataReport.length - 1].sentiment > 0.7
                        ? 'VERY HEALTHY'
                        : dataReport[dataReport.length - 1].sentiment > 0.5
                        ? 'HEALTHY'
                        : dataReport[dataReport.length - 1].sentiment > 0.2
                        ? 'UNHEALTHY'
                        : 'VERY UNHEALTHY'
                      : ''}
                  </Typography> */}
                  {dataReport.length && (
                    <TemperatureChart
                      data={dataReport[dataReport.length - 1].sentiment * 100}
                      label={
                        dataReport[dataReport.length - 1].sentiment > 0.7
                          ? 'Very Healthy'
                          : dataReport[dataReport.length - 1].sentiment > 0.5
                          ? 'Healthy'
                          : dataReport[dataReport.length - 1].sentiment > 0.2
                          ? 'Unhealthy'
                          : 'Very Unhealthy'
                      }
                    />
                  )}
                </Grid>
                <Grid style={{ marginTop: '-60px' }}>
                  <LineMiniChart
                    data={
                      dataReport.length
                        ? dataReport.map((item: any) => {
                            return {
                              x: item.week,
                              y: item.sentiment,
                            };
                          })
                        : []
                    }
                    height={120}
                    strokeWidth={3}
                    color={colors.greyBrand}
                  />
                </Grid>
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper className={classes.metricBoxChart}>
                <Grid className={classes.rowGridMetric}>
                  <Box p={1} flexGrow={1} flexDirection="row" className={classes.titleBox}>
                    <Typography component="h2" className={classes.subtitle}>
                      Energy
                    </Typography>
                    <Typography
                      component="h3"
                      variant="h5"
                      className={classes.whatIs}
                      data-tip={`The Energy Metric represents the way that your team is participating.<br/> The metric
                      is based on activity according to the group's size<br/> and the sentiment of the
                      messages.<br/> A low Energy can represent a lack of participation, absence of
                      members, <br/>or irregular situations during the week.<br/> Keeping good Energy levels
                      means a motivated team with good dynamics.`}
                    >
                      What is it?
                    </Typography>
                  </Box>
                  <Typography
                    className={classes.ctaText}
                    style={{ color: colors.orangeBrand }}
                    onClick={() => history.push(`/dashboard/pillars-report${selectedQuery}`)}
                  >
                    View Details <ArrowRightIcon fontSize="small" />
                  </Typography>
                </Grid>
                {/* <Grid className={classes.metricInside}> */}
                {/* <PillarsAnimations speed={0.4} type="energy" /> */}
                <SenslaChart
                  data={dataReport.length ? [dataReport[dataReport.length - 1].energy] : []}
                  name="Energy"
                  color={colors.orangeBrand}
                />
                {/* <Typography className={classes.textMetric}>
                    {energyText(
                      dataReport.length
                        ? dataReport.map((item: any) => {
                            return { x: item.week, y: item.energy };
                          })
                        : []
                    )}
                  </Typography> */}
                {/* </Grid> */}
                <Grid style={{ marginTop: '-50px' }}>
                  <LineMiniChart
                    data={
                      dataReport.length
                        ? dataReport.map((item: any) => {
                            return { x: item.week, y: item.energy };
                          })
                        : []
                    }
                    color={colors.orangeBrand}
                    height={120}
                    strokeWidth={3}
                  />
                </Grid>
                <Alert severity={'warning'} icon={false}>
                  {energyText(
                    dataReport.length
                      ? dataReport.map((item: any) => {
                          return { x: item.week, y: item.energy };
                        })
                      : []
                  )}
                </Alert>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.metricBoxChart}>
                <Grid className={classes.rowGridMetric}>
                  <Box p={1} flexGrow={1} flexDirection="row" className={classes.titleBox}>
                    <Typography component="h2" className={classes.subtitle}>
                      Efficacy
                    </Typography>
                    <Typography
                      component="h3"
                      variant="h5"
                      className={classes.whatIs}
                      data-tip={`Efficacy is a metric that could be associated with teamwork, empathy, and
                      collaboration.<br/> This pillar is based on the team’s capacity to reply to mentions
                      and <br/>the average response time of the involved members. <br/>Remote workers need
                      answers to advance in some specific task or clarify doubts.<br/> Maintaining a good
                      efficacy can improve the job of other teammates<br/> to avoid misunderstanding or
                      reworks.`}
                    >
                      What is it?
                    </Typography>
                  </Box>
                  <Typography
                    className={classes.ctaText}
                    style={{ color: colors.redBrand }}
                    onClick={() => history.push(`/dashboard/pillars-report${selectedQuery}`)}
                  >
                    View Details <ArrowRightIcon fontSize="small" />
                  </Typography>
                </Grid>
                {/* <Grid className={classes.metricInside}> */}
                {/* <PillarsAnimations speed={0.4} type="efficacy" /> */}
                <SenslaChart
                  data={dataReport.length ? [dataReport[dataReport.length - 1].efficacy] : []}
                  name="Efficacy"
                  color={colors.redBrand}
                />
                {/* <Typography className={classes.textMetric}>
                    {efficacyText(
                      dataReport.length
                        ? dataReport.map((item: any) => {
                            return { x: item.week, y: item.efficacy };
                          })
                        : []
                    )}
                  </Typography> */}
                {/* </Grid> */}
                <Grid style={{ marginTop: '-50px' }}>
                  <LineMiniChart
                    data={
                      dataReport.length
                        ? dataReport.map((item: any) => {
                            return { x: item.week, y: item.efficacy };
                          })
                        : []
                    }
                    color={colors.redBrand}
                    height={120}
                    strokeWidth={3}
                  />
                </Grid>
                <Alert severity={'error'} icon={false}>
                  {efficacyText(
                    dataReport.length
                      ? dataReport.map((item: any) => {
                          return { x: item.week, y: item.efficacy };
                        })
                      : []
                  )}
                </Alert>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.metricBoxChart}>
                <Grid className={classes.rowGridMetric}>
                  <Box p={1} flexGrow={1} flexDirection="row" className={classes.titleBox}>
                    <Typography component="h2" className={classes.subtitle}>
                      Constancy
                    </Typography>
                    <Typography
                      component="h3"
                      variant="h5"
                      className={classes.whatIs}
                      data-tip={`Constancy in remote workers means the capacity to be in place, <br/>interacting with
                      others day by day, maintaining the daily work in order. <br/>Constancy does not mean
                      extra work with extra time.<br/> Lower Constancy can determine the absence of the
                      team or some members of it.<br/> We define it with the number of employees that are
                      interacting<br/> within the group distributed by weekdays.`}
                    >
                      What is it?
                    </Typography>
                  </Box>
                  <Typography
                    className={classes.ctaText}
                    style={{ color: colors.greenBrand }}
                    onClick={() => history.push(`/dashboard/pillars-report${selectedQuery}`)}
                  >
                    View Details <ArrowRightIcon fontSize="small" />
                  </Typography>
                </Grid>
                {/* <Grid className={classes.metricInside}> */}
                {/* <PillarsAnimations speed={0.4} type="constancy" /> */}
                <SenslaChart
                  data={dataReport.length ? [dataReport[dataReport.length - 1].constancy] : []}
                  name="Constancy"
                  color={colors.greenBrand}
                />
                {/* <Typography className={classes.textMetric}>
                    {constancyText(
                      dataReport.length
                        ? dataReport.map((item: any) => {
                            return { x: item.week, y: item.constancy };
                          })
                        : []
                    )}
                  </Typography> */}
                {/* </Grid> */}
                <Grid style={{ marginTop: '-50px' }}>
                  <LineMiniChart
                    data={
                      dataReport.length
                        ? dataReport.map((item: any) => {
                            return { x: item.week, y: item.constancy };
                          })
                        : []
                    }
                    color={colors.greenBrand}
                    height={120}
                    strokeWidth={3}
                  />
                </Grid>
                <Alert severity={'success'} icon={false}>
                  {constancyText(
                    dataReport.length
                      ? dataReport.map((item: any) => {
                          return { x: item.week, y: item.constancy };
                        })
                      : []
                  )}
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : (
        <div className={classes.loading}>
          <Loading containerHeight="70vh" />
        </div>
      )}
    </div>
  );
};

export { Pillars };
